import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

interface IPhoneFrameProps {
  children: ReactNode
  className?: string
}

// iPhone 17 SVG from Wikimedia Commons (CC BY-SA 4.0)
// Original: https://commons.wikimedia.org/wiki/File:IPhone_17_Vector.svg
// Author: Rafael Fernandez (TheGoldenBox)
function IPhone17SVG({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 72.5 150"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="frame-gradient" x1="1.76" y1="30.85" x2="1.76" y2="37.83" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#686171" />
          <stop offset="1" stopColor="#575360" />
        </linearGradient>
        <linearGradient id="frame-highlight" x1="-6.85" y1="40.49" x2="-6.85" y2="47.09" gradientUnits="userSpaceOnUse" gradientTransform="matrix(1,0,0,1.02,10.53,-10.43)">
          <stop offset="0" stopColor="#867e90" />
          <stop offset="0.17" stopColor="#c5c4d1" />
          <stop offset="0.65" stopColor="#8d8698" />
          <stop offset="1" stopColor="#7a7184" />
        </linearGradient>
        <linearGradient id="bezel-gradient" x1="43.68" y1="7.43" x2="43.68" y2="7.43" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#39394e" />
          <stop offset="1" stopColor="#1e1e21" />
        </linearGradient>
        <radialGradient id="lens-gradient" cx="48.73" cy="7.82" r="0.68" gradientUnits="userSpaceOnUse" gradientTransform="matrix(1,0,0,1.36,-5.2,-14.35)">
          <stop offset="0" stopColor="#b697ff" />
          <stop offset="0.56" stopColor="#3600ae" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <clipPath id="screen-clip">
          <rect x="0.45" y="0" width="71.61" height="150" rx="12.87" />
        </clipPath>
      </defs>

      {/* Side buttons - left */}
      <g>
        <rect fill="url(#frame-gradient)" width="7.64" height="6.97" y="30.69" rx="0.31" />
        <rect fill="url(#frame-highlight)" width="7.23" height="6.72" x="0.06" y="30.82" rx="0.3" />
      </g>
      <g transform="translate(-0.25)">
        <path fill="url(#frame-gradient)" d="M0.56,42.77H7.58c0.17,0,0.31,0.14,0.31,0.31v10.63c0,0.17-0.14,0.31-0.31,0.31H0.56c-0.17,0-0.31-0.14-0.31-0.31V43.08c0-0.17,0.14-0.31,0.31-0.31z" />
        <path fill="url(#frame-highlight)" d="M0.61,42.9H7.24c0.16,0,0.3,0.13,0.3,0.3v10.4c0,0.16-0.13,0.3-0.3,0.3H0.61c-0.16,0-0.3-0.13-0.3-0.3V43.2c0-0.16,0.13-0.3,0.3-0.3z" />
      </g>
      <g transform="translate(-0.25,14.23)">
        <path fill="url(#frame-gradient)" d="M0.56,42.77H7.58c0.17,0,0.31,0.14,0.31,0.31v10.63c0,0.17-0.14,0.31-0.31,0.31H0.56c-0.17,0-0.31-0.14-0.31-0.31V43.08c0-0.17,0.14-0.31,0.31-0.31z" />
        <path fill="url(#frame-highlight)" d="M0.61,42.9H7.24c0.16,0,0.3,0.13,0.3,0.3v10.4c0,0.16-0.13,0.3-0.3,0.3H0.61c-0.16,0-0.3-0.13-0.3-0.3V43.2c0-0.16,0.13-0.3,0.3-0.3z" />
      </g>

      {/* Side button - right (power) */}
      <g transform="matrix(-1,0,0,1,72.75,3.82)">
        <path fill="url(#frame-gradient)" d="M0.56,42.77H7.58c0.17,0,0.31,0.14,0.31,0.31v17.18c0,0.17-0.14,0.31-0.31,0.31H0.56c-0.17,0-0.31-0.14-0.31-0.31V43.08c0-0.17,0.14-0.31,0.31-0.31z" />
        <path fill="url(#frame-highlight)" d="M0.61,42.9H7.24c0.16,0,0.3,0.13,0.3,0.3v16.95c0,0.16-0.13,0.3-0.3,0.3H0.61c-0.16,0-0.3-0.13-0.3-0.3V43.2c0-0.16,0.13-0.3,0.3-0.3z" />
      </g>

      {/* Phone body - outer frame (lavender titanium) */}
      <rect fill="#716a7b" width="71.61" height="150" x="0.45" rx="12.87" />

      {/* Frame highlights */}
      <g clipPath="url(#screen-clip)">
        <path fill="#9486a7" fillOpacity="0.6" d="M13.31,0.5c-6.86,0-12.37,5.51-12.37,12.37V137.13c0,6.86,5.51,12.37,12.37,12.37h45.88c6.86,0,12.37-5.51,12.37-12.37V12.87c0-6.86-5.51-12.37-12.37-12.37z" />
        <path fill="#f1eef4" fillOpacity="0.3" d="M13.31,0.9c-6.64,0-11.97,5.32-11.97,11.97V137.13c0,6.64,5.32,11.96,11.97,11.96h45.88c6.64,0,11.97-5.32,11.97-11.96V12.87c0-6.64-5.32-11.97-11.97-11.97z" />
      </g>

      {/* Frame edge details */}
      <path fill="#e7e3ec" d="M56.06,0V15.53H0.45v1.05H72.05V15.53H57.11V0zM0.45,133.42v1.05H15.39v15.53h1.05v-15.53h55.61v-1.05z" />

      {/* Screen bezel (black) */}
      <path fill="#000" d="M13.31,0.98c-6.6,0-11.89,5.29-11.89,11.89V137.13c0,6.6,5.29,11.89,11.89,11.89h45.88c6.6,0,11.89-5.29,11.89-11.89V12.87c0-6.6-5.29-11.89-11.89-11.89z" />

      {/* Top notch reflection */}
      <path fill="#fff" fillOpacity="0.05" d="M29.07,0.98c0.1,0,0.29,0.56,0.4,0.56h13.56c0.11,0,0.3-0.56,0.4-0.56z" />

      {/* Inner screen frame (dark gray) */}
      <path fill="#333" d="M13.31,2.46c-5.8,0-10.4,4.6-10.4,10.4V137.13c0,5.8,4.6,10.4,10.4,10.4h45.87c5.8,0,10.4-4.6,10.4-10.4V12.87c0-5.8-4.6-10.4-10.4-10.4zM28.82,4.8h14.86c1.67,0,3.01,1.34,3.01,3.01s-1.34,3.01-3.01,3.01H28.82c-1.67,0-3.01-1.34-3.01-3.01s1.34-3.01,3.01-3.01z" />

      {/* Camera lens */}
      <circle fill="#261e1e" cx="43.68" cy="7.82" r="1.55" />
      <circle fill="url(#bezel-gradient)" cx="43.68" cy="7.82" r="0.92" />
    </svg>
  )
}

export function IPhoneFrame({ children, className }: IPhoneFrameProps) {
  // iPhone 17 dimensions: 149.6mm tall × 71.5mm wide (ratio 2.092)
  // Sized to fit preview panel with correct proportions
  const phoneWidth = 340
  const phoneHeight = 711

  // Screen area within SVG (calculated from path coordinates)
  // Screen starts at x=2.9, y=2.5 and ends at x=69.6, y=147.5
  const screenLeft = 2
  const screenTop = 1.7
  const screenRight = 2
  const screenBottom = 2.2

  return (
    <div
      className={cn('relative flex-shrink-0', className)}
      style={{ width: phoneWidth, height: phoneHeight }}
    >
      {/* iPhone 17 SVG frame */}
      <IPhone17SVG className="absolute inset-0 w-full h-full" />

      {/* Screen content area - positioned to match the black screen area in SVG */}
      <div
        className="absolute overflow-hidden"
        style={{
          left: `${screenLeft}%`,
          top: `${screenTop}%`,
          right: `${screenRight}%`,
          bottom: `${screenBottom}%`,
          borderRadius: '48px',
        }}
      >
        {children}
      </div>

      {/* Home indicator */}
      <div
        className="absolute left-1/2 -translate-x-1/2 w-[100px] h-[4px] bg-white/40 rounded-full"
        style={{ bottom: '5px' }}
      />
    </div>
  )
}
